import { CopyOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Spin, Tooltip, message } from "antd";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";

const SchemaHealthReportModal = ({ open, onClose, reportHtml, loading, error, onRegenerate }) => {
    const extractTextFromHtml = (html) => {
        if (!html) return "";
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.body.textContent || "";
    };

    const handleCopyText = () => {
        if (reportHtml) {
            const textContent = extractTextFromHtml(reportHtml);
            navigator.clipboard
                .writeText(textContent)
                .then(() => {
                    message.success("Health report copied to clipboard");
                })
                .catch(() => {
                    message.error("Failed to copy to clipboard");
                });
        }
    };
    const iframeRef = useRef(null);

    const adjustIframeHeight = useCallback(() => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
            const height = iframe.contentDocument.body.scrollHeight;
            iframe.style.height = `${height + 20}px`;
        }
    }, []);

    useEffect(() => {
        if (reportHtml && iframeRef.current) {
            const iframe = iframeRef.current;

            const handleLoad = () => {
                adjustIframeHeight();
            };

            iframe.addEventListener("load", handleLoad);
            return () => iframe.removeEventListener("load", handleLoad);
        }
    }, [reportHtml, adjustIframeHeight]);

    const buildPrintSafeHtml = useCallback((html) => {
        if (!html) return html;
        return html.replace(
            "</head>",
            "<style>html,body{overflow-x:hidden;max-width:100%;word-wrap:break-word}td,th{overflow-wrap:break-word}</style></head>",
        );
    }, []);

    const handleDownloadPdf = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.contentWindow) return;

        const printWindow = window.open("", "_blank");
        if (!printWindow) return;

        printWindow.document.write(buildPrintSafeHtml(reportHtml));
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
            }, 300);
        };
    }, [reportHtml, buildPrintSafeHtml]);

    const sanitizedHtml = buildPrintSafeHtml(reportHtml);

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">✨ Governance Health Report</span>
                </div>
            }
            open={open}
            onCancel={onClose}
            width={1000}
            centered
            footer={
                <div className="flex justify-between">
                    <Button type="default" icon={<ReloadOutlined />} onClick={onRegenerate} loading={loading} disabled={loading}>
                        Regenerate Report
                    </Button>
                    <Space>
                        {reportHtml && !loading && !error && (
                            <Tooltip title="Copy report to clipboard">
                                <Button type="default" icon={<CopyOutlined />} onClick={handleCopyText}>
                                    Copy
                                </Button>
                            </Tooltip>
                        )}
                        {reportHtml && !loading && !error && (
                            <Button type="default" icon={<DownloadOutlined />} onClick={handleDownloadPdf}>
                                Download PDF
                            </Button>
                        )}
                        <Button type="primary" onClick={onClose}>
                            Close
                        </Button>
                    </Space>
                </div>
            }
            styles={{
                body: {
                    maxHeight: "75vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: 0,
                },
            }}
        >
            {loading ? (
                <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                    <Spin size="large" />
                    <div className="text-center">
                        <p className="text-base font-medium text-gray-700">Generating governance health report…</p>
                        <p className="mt-1 text-sm text-gray-500">This may take a few seconds for first-time generation.</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex min-h-[300px] flex-col items-center justify-center gap-4">
                    <div className="text-center">
                        <p className="text-base font-medium text-red-600">Failed to generate health report.</p>
                        <p className="mt-1 text-sm text-gray-500">Please try again or contact support if the issue persists.</p>
                    </div>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={onRegenerate}>
                        Retry
                    </Button>
                </div>
            ) : reportHtml ? (
                <iframe
                    ref={iframeRef}
                    sandbox="allow-same-origin"
                    srcDoc={sanitizedHtml}
                    title="Schema Health Report"
                    style={{
                        width: "100%",
                        border: "none",
                        overflow: "hidden",
                    }}
                />
            ) : null}
        </Modal>
    );
};

SchemaHealthReportModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    reportHtml: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    onRegenerate: PropTypes.func.isRequired,
};

export default React.memo(SchemaHealthReportModal);
